import { ApiError, type CannedReply, type Thread, type ThreadKind } from '@central/api';
import { Card, charCount } from '@central/ui';
import { Alert, Button, Group, Modal, Select, Text, TextInput, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type RefObject } from 'react';
import { keys, useApi } from '../api';
import {
  cannedKindLabel,
  cannedKindOptions,
  insertText,
  placeholders,
  renderCannedReply,
  threadValues,
} from '../lib/cannedReply';
import { errorMessage } from '../lib/errors';
import { muted } from '../lib/labels';
import { useIsReseller, useMe } from '../lib/me';
import { Empty, QueryState } from './parts';

const MAX_NAME = 80;
const MAX_BODY = 20_000;

/**
 * "Insert reply": a searchable list of the canned replies for thread t's
 * kind. Choosing one renders its placeholders for t and puts it at the
 * textarea's caret (over its selection), or appends it when there is no
 * textarea; onChange gets the new composer text. Canned replies are
 * Rethink's, so a Reseller-role user is not offered them.
 */
export function CannedReplyPicker({
  t,
  value,
  onChange,
  textarea,
  disabled,
}: {
  t: Thread;
  value: string;
  onChange: (next: string) => void;
  textarea: RefObject<HTMLTextAreaElement | null>;
  disabled?: boolean;
}) {
  const api = useApi();
  const me = useMe();
  const reseller = useIsReseller();
  const replies = useQuery({
    queryKey: keys.cannedReplyList(t.kind),
    queryFn: () => api.cannedReplies.list({ kind: t.kind }),
    staleTime: 60_000,
    enabled: !reseller,
  });
  const items = replies.data?.items ?? [];
  const choose = (id: string | null) => {
    const r = items.find((i) => i.id === id);
    if (!r) return;
    const el = textarea.current;
    const text = renderCannedReply(r.body, threadValues(t, me));
    const next = insertText(value, text, el?.selectionStart, el?.selectionEnd);
    onChange(next.value);
    if (el)
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(next.caret, next.caret);
      });
  };
  if (reseller) return null;
  return (
    <Select
      aria-label="Insert reply"
      placeholder={items.length ? 'Insert reply…' : 'No canned replies'}
      searchable
      clearable={false}
      value={null}
      onChange={choose}
      data={items.map((r) => ({ value: r.id, label: r.name }))}
      nothingFoundMessage="No canned reply matches."
      disabled={disabled || items.length === 0}
      comboboxProps={{ width: 320, position: 'top-start' }}
      style={{ width: 220, alignSelf: 'flex-start' }}
    />
  );
}

/**
 * Settings section (Owner and Admin): create, edit and delete the canned
 * replies every staff role inserts from the inbox composer.
 */
export function CannedRepliesSettings() {
  const api = useApi();
  const list = useQuery({ queryKey: keys.cannedReplies, queryFn: () => api.cannedReplies.list() });
  const [editing, setEditing] = useState<CannedReply | 'new'>();
  const items = list.data?.items ?? [];
  return (
    <Card title="Canned replies">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Text size="sm" style={muted}>
          Saved responses staff insert into a reply or internal note from the inbox. Placeholders
          are filled in from the conversation.
        </Text>
        <QueryState isPending={list.isPending} error={list.error}>
          {() =>
            items.length === 0 ? (
              <Empty>No canned replies yet.</Empty>
            ) : (
              <ul
                aria-label="Canned replies"
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                {items.map((r) => (
                  <CannedRow key={r.id} r={r} onEdit={() => setEditing(r)} />
                ))}
              </ul>
            )
          }
        </QueryState>
        <Group>
          <Button color="dark" onClick={() => setEditing('new')}>
            New canned reply
          </Button>
        </Group>
      </div>
      {editing && (
        <CannedReplyForm
          reply={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(undefined)}
        />
      )}
    </Card>
  );
}

function CannedRow({ r, onEdit }: { r: CannedReply; onEdit: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.cannedReplies.delete(r.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.cannedReplies }),
  });
  const excerpt = r.body.split(/\s+/).join(' ');
  return (
    <li
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: '1 1 240px' }}
      >
        <b style={{ fontSize: 14 }}>{r.name}</b>
        <span style={{ fontSize: 13, ...muted }}>
          {cannedKindLabel(r.kind)} · {excerpt.length > 120 ? `${excerpt.slice(0, 119)}…` : excerpt}
        </span>
        {remove.error && <Alert color="red">{errorMessage(remove.error)}</Alert>}
      </div>
      <Group gap={8}>
        {confirm ? (
          <>
            <span style={{ fontSize: 13 }}>Delete it?</span>
            <Button
              size="xs"
              color="red"
              loading={remove.isPending}
              onClick={() => remove.mutate()}
              aria-label={`Confirm delete ${r.name}`}
            >
              Delete
            </Button>
            <Button size="xs" variant="default" onClick={() => setConfirm(false)}>
              Keep
            </Button>
          </>
        ) : (
          <>
            <Button size="xs" variant="default" onClick={onEdit} aria-label={`Edit ${r.name}`}>
              Edit
            </Button>
            <Button
              size="xs"
              variant="subtle"
              color="red"
              onClick={() => setConfirm(true)}
              aria-label={`Delete ${r.name}`}
            >
              Delete
            </Button>
          </>
        )}
      </Group>
    </li>
  );
}

type Fields = Partial<Record<'name' | 'body' | 'kind', string>>;

function validate(name: string, body: string): Fields {
  const f: Fields = {};
  if (!name) f.name = 'Name the reply.';
  else if (charCount(name) > MAX_NAME) f.name = `At most ${MAX_NAME} characters.`;
  else if (/[\r\n]/.test(name)) f.name = 'Keep the name on one line.';
  if (!body) f.body = 'Write the reply.';
  else if (charCount(body) > MAX_BODY) f.body = 'Keep it under 20,000 characters.';
  return f;
}

function CannedReplyForm({ reply, onClose }: { reply?: CannedReply; onClose: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const [name, setName] = useState(reply?.name ?? '');
  const [body, setBody] = useState(reply?.body ?? '');
  const [kind, setKind] = useState<ThreadKind | ''>(reply?.kind ?? '');
  const [fields, setFields] = useState<Fields>({});
  const save = useMutation({
    mutationFn: () => {
      const b = { name: name.trim(), body: body.trim() };
      return reply
        ? api.cannedReplies.update(reply.id, { ...b, kind })
        : api.cannedReplies.create({ ...b, kind: kind || undefined });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.cannedReplies });
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'duplicate')
        setFields({ name: 'Another canned reply has this name.' });
      else if (e instanceof ApiError && e.fields) setFields(e.fields);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const f = validate(name.trim(), body.trim());
    setFields(f);
    if (Object.keys(f).length === 0 && !save.isPending) save.mutate();
  };
  const fieldError =
    save.error instanceof ApiError && (save.error.code === 'duplicate' || save.error.fields);
  return (
    <Modal
      opened
      onClose={onClose}
      title={reply ? `Edit ${reply.name}` : 'New canned reply'}
      size="lg"
      closeButtonProps={{ 'aria-label': 'Close' }}
    >
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <TextInput
          label="Name"
          placeholder="Ask for service logs"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          error={fields.name}
        />
        <Select
          label="Use for"
          data={[{ value: '', label: 'Every kind' }, ...cannedKindOptions]}
          value={kind}
          onChange={(v) => setKind((v ?? '') as ThreadKind | '')}
          allowDeselect={false}
          error={fields.kind}
          style={{ maxWidth: 260 }}
        />
        <Textarea
          label="Reply"
          autosize
          minRows={5}
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
          error={fields.body}
          description={
            <>
              Placeholders:{' '}
              {placeholders.map((p, i) => (
                <span key={p.key}>
                  {i > 0 && ', '}
                  <code>{`{{${p.key}}}`}</code> {p.help}
                </span>
              ))}
              . Any other {'{{…}}'} stays as typed.
            </>
          }
        />
        {save.error && !fieldError && <Alert color="red">{errorMessage(save.error)}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="dark" loading={save.isPending}>
            {reply ? 'Save canned reply' : 'Create canned reply'}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
